import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx-js-style';
import { stripHtml as sanitizeStripHtml } from '../../utils/sanitize';
import { bandwidthExportDateFileSuffix } from '../bandwidth/bandwidthExportFileNaming';
import type {
  KpiDrilldownTicketDto,
  OpenBacklogDetailExportKind,
  OpenBacklogDetailRow,
} from './kpiDashboardApi';
import { applyYellowHeaderRowAndAutofilter, setSheetColumnWidths } from './kpiDashboardExcelExport';
import type { KpiLastUpdatedRow, KpiOperationalAgeingRow } from './types';
import {
  type TicketType,
  changeStateLabels,
  closureCodeLabels,
  channelLabels,
} from '../dashboard/types/pulseReport';

const PDF_HEADER_BG: [number, number, number] = [68, 114, 196];
const PDF_HEADER_TEXT: [number, number, number] = [255, 255, 255];
/** Match `kpiDashboardExcelExport` yellow header (#FFFF00, black text). */
const PDF_YELLOW_HEADER_BG: [number, number, number] = [255, 255, 0];
const PDF_YELLOW_HEADER_TEXT: [number, number, number] = [0, 0, 0];
const PDF_TITLE: [number, number, number] = [30, 41, 59];
const PDF_BODY_TEXT: [number, number, number] = [15, 23, 42];
const PDF_MUTED: [number, number, number] = [100, 116, 139];
const PDF_BORDER: [number, number, number] = [199, 210, 254];
const PDF_ZEBRA_A: [number, number, number] = [255, 255, 255];
const PDF_ZEBRA_B: [number, number, number] = [248, 250, 252];

function pdfAsciiSafeText(s: string): string {
  return s
    .replace(/\u2192/g, ' to ')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ');
}

function periodSubtitle(dateFrom: string, dateTo: string): string {
  const d1 = (dateFrom || '').trim().slice(0, 10) || 'n/a';
  const d2 = (dateTo || '').trim().slice(0, 10) || 'n/a';
  return `Duration: ${d1} to ${d2}`;
}

function drawDrilldownPdfTitle(
  doc: jsPDF,
  title: string,
  dateFrom: string,
  dateTo: string,
  extraLines: string[]
): number {
  const margin = 14;
  let y = 12;
  doc.setTextColor(...PDF_TITLE);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(pdfAsciiSafeText(title), margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF_MUTED);
  y += 5.5;
  doc.text(periodSubtitle(dateFrom, dateTo), margin, y);
  for (const line of extraLines) {
    y += 4;
    doc.setFontSize(8);
    doc.text(pdfAsciiSafeText(line), margin, y);
  }
  doc.setTextColor(...PDF_BODY_TEXT);
  return y + 6;
}

function finalizeDrilldownPdfFooters(doc: jsPDF): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  const generated = new Date().toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_MUTED);
    doc.text(pdfAsciiSafeText(`Generated ${generated}`), 14, pageH - 8);
    doc.text(`Page ${i} of ${total}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.setDrawColor(...PDF_BORDER);
    doc.setLineWidth(0.25);
    doc.line(14, pageH - 11, pageW - 14, pageH - 11);
    doc.setTextColor(...PDF_BODY_TEXT);
  }
}

const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '-';
  const result = sanitizeStripHtml(html);
  return result || '-';
};

const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

export function buildKpiDrilldownPulseExportRows(
  tickets: KpiDrilldownTicketDto[],
  formatDateTime: (v: string | null | undefined) => string
): { headers: string[]; rows: (string | number)[][] } {
  const headers = [
    'Task Type',
    'Number',
    'Short Description',
    'Type',
    'Priority',
    'State',
    'Assignment Group',
    'Assigned To',
    'Company',
    'Customer Contact',
    'R Start',
    'R Target',
    'R Status',
    'R Breached At',
    'R Reason',
    'O Start',
    'O Target',
    'O Status',
    'O Breached At',
    'O Reason',
    'S Start',
    'S Target',
    'S Status',
    'S Breached At',
    'S Reason',
    'Created',
    'Created By',
    'Last Updated Time',
    'Last Updated By',
    'Channel',
    'Resource',
    'Ticket Created',
    'Ticket Resolved',
    'Resolved By',
    'Ticket Closed',
    'Closed By',
    'On Hold Reason',
    'Closure Remarks',
    'Ticket Duration (Mins)',
    'Change Status',
    'Planned Start Date',
    'Planned End Date',
    'Actual Start Date',
    'Actual End Date',
    'Closure Code',
    'RCA Presented By',
    'Problem Date',
  ];

  const rows = tickets.map((t) => {
    const { responseSla: r, ongoingSla: o, resolutionSla: s } = t;
    const tt = t.ticketType as TicketType;
    const na = '-';
    const holdCsv = t.onHoldReason ? sanitizeStripHtml(t.onHoldReason) : '-';
    const closureCsv = t.ticketClosureRemarks ? sanitizeStripHtml(t.ticketClosureRemarks) : '-';
    return [
      t.taskTypeLabel,
      t.number,
      stripHtml(t.shortDescription || ''),
      t.typeDisplay,
      t.priorityLabel,
      t.stateLabel,
      t.assignmentGroup || '-',
      t.assignedTo || '-',
      t.company || '-',
      t.customerContact || '-',
      formatDateTime(r.startTime),
      formatDateTime(r.targetTime),
      r.status,
      formatDateTime(r.breachedAt),
      r.breachReason ? sanitizeStripHtml(r.breachReason) : '-',
      formatDateTime(o.startTime),
      formatDateTime(o.targetTime),
      o.status,
      formatDateTime(o.breachedAt),
      o.breachReason ? sanitizeStripHtml(o.breachReason) : '-',
      formatDateTime(s.startTime),
      formatDateTime(s.targetTime),
      s.status,
      formatDateTime(s.breachedAt),
      s.breachReason ? sanitizeStripHtml(s.breachReason) : '-',
      formatDateTime(t.createdAt),
      t.createdBy || na,
      formatDateTime(t.ticketLastUpdatedTime),
      t.lastUpdatedBy || na,
      t.channel ? channelLabels[t.channel] || t.channel : na,
      t.resource || na,
      formatDateTime(t.ticketCreated),
      formatDateTime(t.ticketResolved),
      t.ticketResolvedBy || na,
      formatDateTime(t.ticketClosed),
      t.ticketClosedBy || na,
      holdCsv,
      closureCsv,
      formatDuration(t.ticketDurationMins),
      tt === 'CHANGE'
        ? t.changeStatus
          ? changeStateLabels[t.changeStatus] || t.changeStatus
          : na
        : na,
      tt === 'CHANGE' ? formatDateTime(t.plannedStartDate) : na,
      tt === 'CHANGE' ? formatDateTime(t.plannedEndDate) : na,
      tt === 'CHANGE' ? formatDateTime(t.actualStartDate) : na,
      tt === 'CHANGE' ? formatDateTime(t.actualEndDate) : na,
      tt !== 'INCIDENT' && tt !== 'SERVICE_REQUEST'
        ? t.closureCode
          ? closureCodeLabels[t.closureCode] || t.closureCode
          : na
        : na,
      tt === 'PROBLEM' ? t.rcaPresentedBy || na : na,
      tt === 'PROBLEM' ? formatDateTime(t.problemDate) : na,
    ];
  });

  return { headers, rows };
}

export function exportKpiDrilldownTicketsExcel(
  tickets: KpiDrilldownTicketDto[],
  formatDateTime: (v: string | null | undefined) => string,
  fileBase: string
): void {
  const { headers, rows } = buildKpiDrilldownPulseExportRows(tickets, formatDateTime);
  const aoa: (string | number)[][] = [headers, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  applyYellowHeaderRowAndAutofilter(ws, headers.length, aoa.length);
  setSheetColumnWidths(
    ws,
    headers.map((_, i) => (i === 2 ? 48 : i >= 10 && i <= 24 ? 14 : 12))
  );
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${fileBase}_${stamp}.xlsx`, { cellStyles: true });
}

export function exportKpiDrilldownTicketsCsv(
  tickets: KpiDrilldownTicketDto[],
  formatDateTime: (v: string | null | undefined) => string,
  fileBase: string
): { blob: Blob; fileName: string } {
  const { headers, rows } = buildKpiDrilldownPulseExportRows(tickets, formatDateTime);
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `${fileBase}_${stamp}.csv`;
  return { blob, fileName };
}

export function exportKpiDrilldownTicketsPdf(params: {
  tickets: KpiDrilldownTicketDto[];
  formatDateTime: (v: string | null | undefined) => string;
  filterLabel: string;
  pageNote: string;
  dateFrom: string;
  dateTo: string;
  fileBase: string;
}): void {
  const { tickets, formatDateTime, filterLabel, pageNote, dateFrom, dateTo, fileBase } = params;
  if (tickets.length === 0) return;

  const { headers, rows } = buildKpiDrilldownPulseExportRows(tickets, formatDateTime);
  const head = [headers.map((h) => pdfAsciiSafeText(h))];
  const body = rows.map((r) => r.map((c) => pdfAsciiSafeText(String(c ?? ''))));

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawDrilldownPdfTitle(doc, `KPI Drill-down - ${filterLabel}`, dateFrom, dateTo, [
    pageNote,
    'Wide table: columns repeat on horizontal page breaks. Use Excel for single-sheet copy.',
  ]);

  autoTable(doc, {
    theme: 'plain',
    startY,
    head,
    body,
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: [0, 1, 2],
    styles: {
      fontSize: 5,
      cellPadding: 0.6,
      textColor: PDF_BODY_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PDF_HEADER_BG,
      textColor: PDF_HEADER_TEXT,
      fontStyle: 'bold',
      fontSize: 5,
      cellPadding: 0.6,
    },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeDrilldownPdfFooters(doc);
  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`${fileBase}_${stamp}.pdf`);
}

/** Task type label for filename (ASCII). */
export function kpiDrilldownFileSlug(drillLabel: string): string {
  return (
    drillLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'kpi_drilldown'
  );
}

const TYPE_ORDER_SUMMARY = ['INCIDENT', 'SERVICE_REQUEST'] as const;

function sumAgeingRows(rows: KpiOperationalAgeingRow[]) {
  return rows.reduce(
    (acc, r) => ({
      days2to5: acc.days2to5 + r.days2to5,
      days6to10: acc.days6to10 + r.days6to10,
      days11to15: acc.days11to15 + r.days11to15,
      days16to30: acc.days16to30 + r.days16to30,
      daysOver30: acc.daysOver30 + r.daysOver30,
      rowTotal: acc.rowTotal + r.rowTotal,
    }),
    {
      days2to5: 0,
      days6to10: 0,
      days11to15: 0,
      days16to30: 0,
      daysOver30: 0,
      rowTotal: 0,
    }
  );
}

function sumLastRows(rows: KpiLastUpdatedRow[]) {
  return rows.reduce(
    (acc, r) => ({
      within24h: acc.within24h + r.within24h,
      over24h: acc.over24h + r.over24h,
      rowTotal: acc.rowTotal + r.rowTotal,
    }),
    { within24h: 0, over24h: 0, rowTotal: 0 }
  );
}

function buildAgeingSummaryBody(ageingByGroup: KpiOperationalAgeingRow[]): (string | number)[][] {
  const body: (string | number)[][] = [];
  const ageingByType = new Map<string, KpiOperationalAgeingRow[]>();
  for (const row of ageingByGroup) {
    const list = ageingByType.get(row.ticketType) ?? [];
    list.push(row);
    ageingByType.set(row.ticketType, list);
  }
  for (const tt of TYPE_ORDER_SUMMARY) {
    const rows = ageingByType.get(tt) ?? [];
    if (rows.length === 0) continue;
    for (const r of rows) {
      body.push([
        r.ticketType,
        r.assignmentGroupName,
        r.days2to5,
        r.days6to10,
        r.days11to15,
        r.days16to30,
        r.daysOver30,
        r.rowTotal,
      ]);
    }
    const sub = sumAgeingRows(rows);
    body.push([`${tt} TOTAL`, '', sub.days2to5, sub.days6to10, sub.days11to15, sub.days16to30, sub.daysOver30, sub.rowTotal]);
  }
  const g = sumAgeingRows(ageingByGroup);
  body.push(['GRAND TOTAL', '', g.days2to5, g.days6to10, g.days11to15, g.days16to30, g.daysOver30, g.rowTotal]);
  return body;
}

function buildLastUpdatedSummaryBody(lastUpdatedByGroup: KpiLastUpdatedRow[]): (string | number)[][] {
  const body: (string | number)[][] = [];
  const lastByType = new Map<string, KpiLastUpdatedRow[]>();
  for (const row of lastUpdatedByGroup) {
    const list = lastByType.get(row.ticketType) ?? [];
    list.push(row);
    lastByType.set(row.ticketType, list);
  }
  for (const tt of TYPE_ORDER_SUMMARY) {
    const rows = lastByType.get(tt) ?? [];
    if (rows.length === 0) continue;
    for (const r of rows) {
      body.push([r.ticketType, r.assignmentGroupName, r.within24h, r.over24h, r.rowTotal]);
    }
    const sub = sumLastRows(rows);
    body.push([`${tt} TOTAL`, '', sub.within24h, sub.over24h, sub.rowTotal]);
  }
  const g = sumLastRows(lastUpdatedByGroup);
  body.push(['GRAND TOTAL', '', g.within24h, g.over24h, g.rowTotal]);
  return body;
}

export function exportOperationalAgeingSummaryPdf(params: {
  ageingByGroup: KpiOperationalAgeingRow[];
  dateFrom: string;
  dateTo: string;
}): { blob: Blob; fileName: string } | null {
  const { ageingByGroup, dateFrom, dateTo } = params;
  if (ageingByGroup.length === 0) return null;
  const headers = [
    'Type',
    'Assignment group',
    '2-5 days',
    '6-10 days',
    '11-15 days',
    '16-30 days',
    '>30 days',
    'Grand total',
  ];
  const head = [headers.map((h) => pdfAsciiSafeText(h))];
  const body = buildAgeingSummaryBody(ageingByGroup).map((r) => r.map((c) => pdfAsciiSafeText(String(c ?? ''))));

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const startY = drawDrilldownPdfTitle(doc, 'KPI open backlog - >2 days ageing (summary)', dateFrom, dateTo, [
    'Open incidents and service requests only. Snapshot counts (not limited by KPI date range).',
  ]);

  autoTable(doc, {
    theme: 'plain',
    startY,
    head,
    body,
    styles: {
      fontSize: 8,
      cellPadding: 1.2,
      textColor: PDF_BODY_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PDF_HEADER_BG,
      textColor: PDF_HEADER_TEXT,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 1.2,
    },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeDrilldownPdfFooters(doc);
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `kpi_open_backlog_ageing_summary_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}_${stamp}.pdf`;
  return { blob: doc.output('blob'), fileName };
}

export function exportOperationalLastUpdatedSummaryPdf(params: {
  lastUpdatedByGroup: KpiLastUpdatedRow[];
  dateFrom: string;
  dateTo: string;
}): { blob: Blob; fileName: string } | null {
  const { lastUpdatedByGroup, dateFrom, dateTo } = params;
  if (lastUpdatedByGroup.length === 0) return null;
  const headers = ['Type', 'Assignment group', '0-24 hours', '>24 hours', 'Grand total'];
  const head = [headers.map((h) => pdfAsciiSafeText(h))];
  const body = buildLastUpdatedSummaryBody(lastUpdatedByGroup).map((r) =>
    r.map((c) => pdfAsciiSafeText(String(c ?? '')))
  );

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const startY = drawDrilldownPdfTitle(doc, 'KPI open backlog - last updated (summary)', dateFrom, dateTo, [
    'Open incidents and service requests only. Snapshot counts (not limited by KPI date range).',
  ]);

  autoTable(doc, {
    theme: 'plain',
    startY,
    head,
    body,
    styles: {
      fontSize: 8,
      cellPadding: 1.2,
      textColor: PDF_BODY_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: PDF_YELLOW_HEADER_BG,
      textColor: PDF_YELLOW_HEADER_TEXT,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 1.2,
    },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeDrilldownPdfFooters(doc);
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `kpi_open_backlog_last_updated_summary_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}_${stamp}.pdf`;
  return { blob: doc.output('blob'), fileName };
}

export function exportOpenBacklogDetailPdf(params: {
  rows: OpenBacklogDetailRow[];
  kind: OpenBacklogDetailExportKind;
  formatDateTime: (v: string | null | undefined) => string;
  dateFrom: string;
  dateTo: string;
  truncated?: boolean;
  totalMatched?: number;
}): { blob: Blob; fileName: string } | null {
  const { rows, kind, formatDateTime, dateFrom, dateTo, truncated, totalMatched } = params;
  if (rows.length === 0) return null;

  const headers = [
    'NUMBER',
    'SHORT DESCRIPTION',
    'TYPE',
    'PRIORITY',
    'STATE',
    'ASSIGNMENT GROUP',
    'ASSIGNED TO',
    'COMPANY',
    'LAST UPDATED TIME',
    'LAST UPDATED BY',
    'TICKET CREATED',
    'LAST UPDATED DURATION',
    'LAST UPDATED AGEING',
    'DURATION (DAYS)',
    'AGEING',
    'REMARKS',
  ];
  const head = [headers.map((h) => pdfAsciiSafeText(h))];
  const body = rows.map((r) =>
    [
      r.number,
      stripHtml(r.shortDescription || ''),
      r.type,
      r.priority,
      r.state,
      r.assignmentGroup,
      r.assignedTo ?? '',
      r.company ?? '',
      formatDateTime(r.lastUpdatedTime),
      r.lastUpdatedBy ?? '',
      formatDateTime(r.ticketCreated),
      r.lastUpdatedDuration,
      r.lastUpdatedAgeing,
      String(r.durationDays ?? ''),
      r.ageingBucket,
      stripHtml(r.remarks || ''),
    ].map((c) => pdfAsciiSafeText(String(c ?? '')))
  );

  const title =
    kind === 'ageing'
      ? 'KPI open backlog - >2 days ageing (detail)'
      : 'KPI open backlog - last updated (detail)';
  const extra: string[] = [
    truncated && totalMatched !== undefined
      ? `Export includes ${rows.length.toLocaleString()} of ${totalMatched.toLocaleString()} matched tickets.`
      : `${rows.length.toLocaleString()} ticket(s).`,
  ];

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawDrilldownPdfTitle(doc, title, dateFrom, dateTo, extra);

  const lastUpdatedDetailHeader = kind === 'lastUpdated';

  autoTable(doc, {
    theme: 'plain',
    startY,
    head,
    body,
    styles: {
      fontSize: 6,
      cellPadding: 0.7,
      textColor: PDF_BODY_TEXT,
      lineColor: PDF_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: lastUpdatedDetailHeader ? PDF_YELLOW_HEADER_BG : PDF_HEADER_BG,
      textColor: lastUpdatedDetailHeader ? PDF_YELLOW_HEADER_TEXT : PDF_HEADER_TEXT,
      fontStyle: 'bold',
      fontSize: 6,
      cellPadding: 0.7,
    },
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: [0, 1, 2],
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeDrilldownPdfFooters(doc);
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = kind === 'ageing' ? 'open_backlog_ageing_detail' : 'open_backlog_last_updated_detail';
  const fileName = `kpi_${slug}_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}_${stamp}.pdf`;
  return { blob: doc.output('blob'), fileName };
}
